import { FC, useMemo, memo } from 'react';

import { Ad } from '@adflow/ad';
import { ConsoleAPIGraph } from '@adflow/graphqlgen';
import { AdElement, AdflowAPI, isTextElement } from '@adflow/types';
import { Box } from '@chakra-ui/react';

import { useLoadFontsForAd } from '../../useFonts';
import PreviewOverlay from '../PreviewOverlay';
import SelectOverlay from '../SelectOverlay';
import { useMappedElements } from '../previewutils';

type Props = {
  elements: Array<AdElement>;
  brand: ConsoleAPIGraph.Brand | null;
  fonts: Array<{
    fontFamily: string;
    weight: number;
    style: string;
    stretch?: string;
    src: string;
  }>;
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions | null;
  selectedElementId?: string | null;
  isIndexPreview: boolean;
  forceTopLeftOrigin?: boolean;
  customizations?: Record<string, string> | null;
  svgIdPrefix?: string | null;
};

const PreviewComponent: FC<Props> = ({
  elements,
  brand,
  fonts,
  displayOptions,
  selectedElementId,
  isIndexPreview,
  forceTopLeftOrigin,
  customizations,
  svgIdPrefix,
}) => {
  const customizedElements = useMemo(() => {
    let result = elements;

    // Apply customizations
    if (customizations && Object.keys(customizations).length > 0) {
      result = elements.map((el) => {
        const override = customizations[el.name];
        if (override !== undefined && isTextElement(el)) {
          return {
            ...el,
            data: { ...el.data, content: override },
          };
        }
        return el;
      });
    }

    // Prefix font families in text elements to match the prefixed fonts loaded by useFonts
    // This prevents brand fonts from affecting the rest of the page
    result = result.map((el) => {
      if (isTextElement(el) && el.data.style.fontFamily) {
        return {
          ...el,
          data: {
            ...el.data,
            style: {
              ...el.data.style,
              fontFamily: `adflow-preview-${el.data.style.fontFamily}`,
            },
          },
        };
      }
      return el;
    });

    return result;
  }, [elements, customizations]);

  const mappedElements = useMappedElements(customizedElements, displayOptions, brand);

  const mappedElementsWithoutSvg = useMemo(
    () => mappedElements.filter((element) => element.type != 'SVG'),
    [mappedElements],
  );

  const svgElement = useMemo(() => elements.find((x) => x.type === 'SVG'), [elements]);
  const transformScale = useMemo(() => {
    if (svgElement?.size.width && svgElement?.size.height) {
      const scaleX = svgElement.size.width > 600 ? 600 / svgElement.size.width : 1;
      const scaleY = svgElement.size.height > 600 ? 600 / svgElement.size.height : 1;
      return Math.min(scaleX, scaleY);
    }
    return 1;
  }, [svgElement]);

  const boxWidth = useMemo(
    () => (svgElement?.size.width ? svgElement?.size.width * transformScale : 600),
    [transformScale, svgElement],
  );
  const boxHeight = useMemo(
    () => (svgElement?.size.height ? svgElement?.size.height * transformScale : 600),
    [transformScale, svgElement],
  );
  let transformOrigin =
    isIndexPreview && svgElement?.size.width === 1200 && svgElement?.size.height === 1200
      ? 'top left'
      : isIndexPreview
      ? 'top'
      : 'top left';

  if (forceTopLeftOrigin) {
    transformOrigin = 'top left';
  }

  useLoadFontsForAd(mappedElements, fonts);

  return (
    <Box
      position="relative"
      sx={{
        width: `${boxWidth}px`,
        height: `${boxHeight}px`,
        transform: `scale(${transformScale})`,
        transformOrigin: transformOrigin,
      }}
    >
      <Ad adElements={mappedElements} isPreview={true} svgIdPrefix={svgIdPrefix} />
      {isIndexPreview ? (
        <></>
      ) : (
        <>
          <PreviewOverlay adElements={mappedElementsWithoutSvg} selectedId={selectedElementId} />
          <SelectOverlay adElements={mappedElementsWithoutSvg} />
        </>
      )}
    </Box>
  );
};

PreviewComponent.displayName = 'Preview';

// Memoize the component to prevent unnecessary re-renders
// Use a custom comparison function to efficiently compare props
const Preview = memo(PreviewComponent, (prevProps, nextProps) => {
  if (
    prevProps.isIndexPreview !== nextProps.isIndexPreview ||
    prevProps.forceTopLeftOrigin !== nextProps.forceTopLeftOrigin ||
    prevProps.selectedElementId !== nextProps.selectedElementId ||
    prevProps.svgIdPrefix !== nextProps.svgIdPrefix
  ) {
    return false;
  }

  if (prevProps.brand?._id !== nextProps.brand?._id) {
    return false;
  }

  if (prevProps.displayOptions !== nextProps.displayOptions) {
    return false;
  }

  // Check if fonts array changed
  if (prevProps.fonts !== nextProps.fonts) {
    if (
      prevProps.fonts.length !== nextProps.fonts.length ||
      prevProps.fonts.some((font, index) => font !== nextProps.fonts[index])
    ) {
      return false;
    }
  }

  // Check if elements array reference changed (indicates an element was updated)
  // Since the store creates a new array when updating elements, we need to check
  // if any element reference changed to detect updates
  if (prevProps.elements !== nextProps.elements) {
    // If array reference changed, check if length or any element reference changed
    if (
      prevProps.elements.length !== nextProps.elements.length ||
      prevProps.elements.some((el, index) => el !== nextProps.elements[index])
    ) {
      return false;
    }
  }

  // Compare customizations efficiently
  const prevCustomizations = prevProps.customizations || {};
  const nextCustomizations = nextProps.customizations || {};
  const prevKeys = Object.keys(prevCustomizations);
  const nextKeys = Object.keys(nextCustomizations);

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    if (prevCustomizations[key] !== nextCustomizations[key]) {
      return false;
    }
  }

  // All comparisons passed - props are equal
  return true;
});

export default Preview;
export type { Props as PreviewProps };
